import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import style from './popup.mod.scss'
import { Portal, Button, Tag } from '@components'
import CloseIcon from '@svgs/close.svg'
import { isBrowser, toggleBodyScroll } from '@context/themeContext'

const windowAvailable = isBrowser()

const resolveLinkPath = link => {
  if (!link) return null

  if (link.link && link.link.path) {
    return link.link.path
  }

  if (link.url) {
    return link.url
  }

  return null
}

const Popup = ({ popup, onDismiss, onNeverShow }) => {
  const [isOpen, setIsOpen] = useState(Boolean(popup))
  const [neverShowChecked, setNeverShowChecked] = useState(false)
  const popupId = popup?.id

  useEffect(() => {
    setIsOpen(Boolean(popup))
    setNeverShowChecked(false)
  }, [popupId, popup])

  useEffect(() => {
    if (!windowAvailable) {
      return undefined
    }

    if (isOpen) {
      toggleBodyScroll(true)
    }

    return () => {
      toggleBodyScroll(false)
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    if (typeof onDismiss === 'function' && popupId) {
      onDismiss(popupId)
    }
  }, [onDismiss, popupId])
  const handleNeverShowChange = useCallback(
    event => {
      const { checked } = event.target
      setNeverShowChecked(checked)

      if (checked && typeof onNeverShow === 'function' && popupId) {
        onNeverShow(popupId)
      }
    },
    [onNeverShow, popupId]
  )

  const { heading, content, buttons, headingBackgroundColour, newTag } =
    popup || {}
  const headingId = popupId && heading ? `popup-${popupId}-heading` : undefined

  const headingClasses = useMemo(() => {
    const classes = [style.popup__heading]

    if (headingBackgroundColour?.[0]?.scheme) {
      classes.push(`theme-${headingBackgroundColour[0].scheme}`)
    }

    return classes
  }, [headingBackgroundColour?.scheme])

  const closeButtonClasses = useMemo(() => {
    const classes = [style.popup__close]

    if (headingBackgroundColour?.[0]?.scheme) {
      classes.push(`theme-${headingBackgroundColour[0].scheme}`)
    }

    return classes
  }, [headingBackgroundColour?.scheme])

  const popupButtons = Array.isArray(buttons) ? buttons : []

  if (!popup || !isOpen) {
    return null
  }

  return (
    <Portal selector={'#___gatsby'}>
      <div
        className={style.popup}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <div className={style.popup__overlay} role="presentation" />
        <div className={style.popup__container}>
          <button
            type="button"
            className={closeButtonClasses.join(' ')}
            onClick={handleClose}
            aria-label="Close popup"
          >
            <CloseIcon />
          </button>
          {heading && (
            <div className={headingClasses.join(' ')}>
              <h2 id={headingId}>{heading}</h2>
            </div>
          )}
          {newTag && (
            <div className={style.popup__meta}>
              <Tag inline className={style.popup__tag}>
                New
              </Tag>
            </div>
          )}
          {content && (
            <div
              className={style.popup__content}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
          {popupButtons.length > 0 && (
            <div className={style.popup__actions}>
              {popupButtons.map(button => {
                const destination = resolveLinkPath(button?.link)

                if (!destination) {
                  return null
                }

                const colourScheme = button?.buttonColour?.[0]?.scheme
                  ? button.buttonColour[0].scheme.toLowerCase()
                  : 'default'
                return (
                  <Button
                    key={button.id}
                    to={destination}
                    buttonStyle={colourScheme}
                  >
                    {button?.link?.displayText || 'Learn more'}
                  </Button>
                )
              })}
            </div>
          )}
          {popupId && (
            <div className={style.popup__preferences}>
              <label
                htmlFor={`popup-${popupId}-never-show`}
                className={style.popup__preferencesLabel}
              >
                <input
                  id={`popup-${popupId}-never-show`}
                  type="checkbox"
                  checked={neverShowChecked}
                  onChange={handleNeverShowChange}
                />
                <span>
                  Tick this box if you no longer wish to see this message again
                </span>
              </label>
            </div>
          )}
        </div>
      </div>
    </Portal>
  )
}

Popup.propTypes = {
  popup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    heading: PropTypes.string,
    content: PropTypes.string,
    newTag: PropTypes.bool,
    visibleOnAllPages: PropTypes.bool,
    headingBackgroundColour: PropTypes.shape({
      scheme: PropTypes.string
    }),
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        buttonColour: PropTypes.shape({
          scheme: PropTypes.string
        }),
        link: PropTypes.shape({
          displayText: PropTypes.string,
          url: PropTypes.string,
          link: PropTypes.shape({
            path: PropTypes.string
          })
        })
      })
    ),
    pagesForPopupToAppear: PropTypes.arrayOf(
      PropTypes.shape({
        displayText: PropTypes.string,
        url: PropTypes.string,
        link: PropTypes.shape({
          path: PropTypes.string
        })
      })
    )
  }),
  onDismiss: PropTypes.func,
  onNeverShow: PropTypes.func
}

Popup.defaultProps = {
  popup: null,
  onDismiss: null,
  onNeverShow: null
}

export default Popup
